import React, { useMemo, useState, useCallback, useRef } from 'react';
import './FloatingParticles.css';

const COLORS = ['#0A84FF', '#5856D6', '#30D158', '#7B6CF6', '#5AC8FA', '#FF3B30', '#FF453A'];

const createFullScreenParticles = (count, profile) =>
  Array.from({ length: count }, (_, i) => ({
    id: `${profile.key}-${i}`,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * profile.sizeRange + profile.sizeMin,
    blur: Math.random() * profile.blurRange + profile.blurMin,
    delay: Math.random() * 6,
    duration: Math.random() * 10 + 14,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    opacity: Math.random() * profile.opacityRange + profile.opacityMin,
    glow: Math.random() * profile.glowRange + profile.glowMin,
  }));

const createSideParticles = (side, count, profile) =>
  Array.from({ length: count }, (_, i) => ({
    id: `${side}-${profile.key}-${i}`,
    side,
    x:
      side === 'left'
        ? Math.random() * profile.leftXRange + profile.leftXMin
        : Math.random() * profile.rightXRange + profile.rightXMin,
    y: Math.random() * 100,
    size: Math.random() * profile.sizeRange + profile.sizeMin,
    blur: Math.random() * profile.blurRange + profile.blurMin,
    delay: Math.random() * 6,
    duration: Math.random() * 10 + 14,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    opacity: Math.random() * profile.opacityRange + profile.opacityMin,
    glow: Math.random() * profile.glowRange + profile.glowMin,
  }));

/* Full viewport — farther depth: soft, diffuse */
const BG_PROFILE = {
  key: 'bg',
  sizeMin: 8,
  sizeRange: 18,
  blurMin: 5,
  blurRange: 10,
  opacityMin: 0.12,
  opacityRange: 0.18,
  glowMin: 20,
  glowRange: 36,
};

/* Side bands — closer depth: sharper */
const FG_PROFILE = {
  key: 'fg',
  leftXMin: 35,
  leftXRange: 62,
  rightXMin: 3,
  rightXRange: 62,
  sizeMin: 12,
  sizeRange: 20,
  blurMin: 0,
  blurRange: 3.5,
  opacityMin: 0.3,
  opacityRange: 0.45,
  glowMin: 16,
  glowRange: 24,
};

const Particle = ({ particle }) => (
  <span
    className="floating-particle"
    style={{
      '--x': `${particle.x}%`,
      '--y': `${particle.y}%`,
      '--size': `${particle.size}px`,
      '--blur': `${particle.blur}px`,
      '--delay': `${particle.delay}s`,
      '--duration': `${particle.duration}s`,
      '--color': particle.color,
      '--opacity': particle.opacity,
      '--glow': `${particle.glow}px`,
    }}
  />
);

const ForegroundParticle = ({ particle, onPopComplete }) => {
  const elRef = useRef(null);
  const poppedRef = useRef(false);

  const handlePointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const el = elRef.current;
    if (!el || poppedRef.current) return;
    poppedRef.current = true;

    el.getAnimations().forEach((animation) => animation.cancel());
    el.style.animation = 'none';

    const burstOpacity = Math.min(1, particle.opacity * 1.45);
    const burstFilter = `blur(${particle.blur}px) brightness(1.65)`;

    el.style.transform = 'scale(1.45)';
    el.style.opacity = String(burstOpacity);
    el.style.filter = burstFilter;

    const pop = el.animate(
      [
        {
          transform: 'scale(1.45)',
          opacity: burstOpacity,
          filter: burstFilter,
        },
        {
          transform: 'scale(2.1)',
          opacity: 0,
          filter: 'blur(8px) brightness(1.9)',
        },
      ],
      {
        duration: 160,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'forwards',
      }
    );

    pop.onfinish = () => onPopComplete(particle.id);
  };

  return (
    <span
      ref={elRef}
      className="floating-particle"
      style={{
        '--x': `${particle.x}%`,
        '--y': `${particle.y}%`,
        '--size': `${particle.size}px`,
        '--blur': `${particle.blur}px`,
        '--delay': `${particle.delay}s`,
        '--duration': `${particle.duration}s`,
        '--color': particle.color,
        '--opacity': particle.opacity,
        '--glow': `${particle.glow}px`,
      }}
      onPointerDown={handlePointerDown}
    />
  );
};

const BackgroundField = ({ particles }) => (
  <div className="floating-particles floating-particles--background" aria-hidden="true">
    {particles.map((p) => <Particle key={p.id} particle={p} />)}
  </div>
);

const ForegroundSides = ({ particles, poppedIds, onPopComplete }) => (
  <div className="floating-particles floating-particles--foreground" aria-hidden="true">
    <div className="floating-particles__side floating-particles__side--left">
      {particles
        .filter((p) => p.side === 'left' && !poppedIds.has(p.id))
        .map((p) => (
          <ForegroundParticle key={p.id} particle={p} onPopComplete={onPopComplete} />
        ))}
    </div>
    <div className="floating-particles__side floating-particles__side--right">
      {particles
        .filter((p) => p.side === 'right' && !poppedIds.has(p.id))
        .map((p) => (
          <ForegroundParticle key={p.id} particle={p} onPopComplete={onPopComplete} />
        ))}
    </div>
  </div>
);

const FloatingParticles = () => {
  const [poppedIds, setPoppedIds] = useState(() => new Set());

  const backgroundParticles = useMemo(
    () => createFullScreenParticles(42, BG_PROFILE),
    []
  );

  const foregroundParticles = useMemo(
    () => [
      ...createSideParticles('left', 12, FG_PROFILE),
      ...createSideParticles('right', 12, FG_PROFILE),
    ],
    []
  );

  const handlePopComplete = useCallback((id) => {
    setPoppedIds((prev) => new Set(prev).add(id));
  }, []);

  return (
    <>
      <div className="floating-particles-layer floating-particles-layer--background">
        <BackgroundField particles={backgroundParticles} />
      </div>
      <div className="floating-particles-layer floating-particles-layer--foreground">
        <ForegroundSides
          particles={foregroundParticles}
          poppedIds={poppedIds}
          onPopComplete={handlePopComplete}
        />
      </div>
    </>
  );
};

export default FloatingParticles;
